/* Vittorio Valeri: one persistent drawer, one section renderer, no cart expiry. */
class VVCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeVersion = 0;
    this.renderVersion = 0;
    this.addEventListener('keyup', (event) => { if (event.code === 'Escape') this.close(); });
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const selector = '#cart-icon-bubble, #vvGlobalCartButton';
    document.querySelectorAll(selector).forEach(link => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('click', event => {
      const trigger = event.target.closest(selector);
      if (!trigger || !this.isConnected) return;
      event.preventDefault();
      this.open(trigger);
    });
    document.addEventListener('keydown', event => {
      if (event.code !== 'Space' || !event.target.closest('#cart-icon-bubble')) return;
      event.preventDefault();
      this.open(event.target.closest('#cart-icon-bubble'));
    });
  }

  focusDrawer() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner || !this.classList.contains('active')) return;
    if (typeof trapFocus === 'function') trapFocus(inner, inner.querySelector('.drawer__close') || inner);
  }

  open(triggeredBy) {
    if (!this.isConnected) return;
    if (triggeredBy instanceof HTMLElement) this.setActiveElement(triggeredBy);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden', 'vv-cart-open', 'vv-cart-drawer-open');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => this.focusDrawer());
    document.dispatchEvent(new CustomEvent('vv:cart-open'));
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.closeVersion += 1;
    cancelAnimationFrame(this.focusFrame);
    this.classList.remove('active');
    document.body.classList.remove('vv-cart-open', 'vv-cart-drawer-open');
    if (!document.querySelector('menu-drawer details[open], [data-vv-mobile-drawer].is-open')) {
      document.body.classList.remove('overflow-hidden');
    }
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : undefined);
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    document.dispatchEvent(new CustomEvent('vv:cart-closed'));
  }

  async renderContents(parsedState, options = {}) {
    if (!parsedState?.sections?.['cart-drawer']) {
      // A successful add with a failed section render still preserves the cart.
      if (options.open !== false) window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }
    if (this.lastPayload === parsedState) return;
    this.lastPayload = parsedState;
    const wasOpen = this.classList.contains('active');
    const version = ++this.renderVersion;
    const closeVersion = parsedState.__vvCloseVersion ?? this.closeVersion;
    const html = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
    const freshDrawer = html.querySelector('cart-drawer');
    const freshContent = html.querySelector('#CartDrawer');
    const currentContent = this.querySelector('#CartDrawer');
    if (!freshDrawer || !freshContent || !currentContent) return;
    const scrollTop = this.querySelector('cart-drawer-items')?.scrollTop || 0;
    currentContent.innerHTML = freshContent.innerHTML;
    const count = Number(freshDrawer.dataset.itemCount || 0);
    this.dataset.itemCount = String(count);
    this.classList.toggle('is-empty', count === 0);
    const items = this.querySelector('cart-drawer-items');
    if (items && wasOpen) items.scrollTop = scrollTop;
    const iconHTML = parsedState.sections['cart-icon-bubble'];
    const icon = document.querySelector('#cart-icon-bubble');
    if (iconHTML && icon && !icon.hasAttribute('data-vv-cart-link')) {
      const freshIcon = new DOMParser().parseFromString(iconHTML, 'text/html').querySelector('.shopify-section');
      if (freshIcon) icon.innerHTML = freshIcon.innerHTML;
    }
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { item_count: count, cart: { item_count: count }, rendered: true }
    }));
    try { window.Shopify?.PaymentButton?.init?.(); } catch (_) {}
    const feedback = window.VVCart?.flyToCart(parsedState.__vvFlight);
    if (wasOpen) {
      document.body.classList.add('overflow-hidden', 'vv-cart-open', 'vv-cart-drawer-open');
      this.focusDrawer();
      return;
    }
    if (options.open === false) return;
    const trigger = parsedState.__vvTrigger || window.VVCart?.trigger;
    if (trigger instanceof HTMLElement) this.setActiveElement(trigger);
    window.VVCart?.announce(window.VVCart.messages.added);
    await feedback;
    if (version === this.renderVersion && closeVersion === this.closeVersion && this.isConnected) this.open(trigger);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);
  }
  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }
  setActiveElement(element) { this.activeElement = element; }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', VVCartDrawer);

class VVCartDrawerItems extends CartItems {
  resetQuantityInput(id) {
    const input = this.querySelector('#Drawer-quantity-' + id);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  onCartUpdate(event) {
    const drawer = this.closest('cart-drawer');
    if (event?.cartData?.sections?.['cart-drawer']) return; // The adding component renders its bundled response.
    if (!drawer) return;
    const version = drawer.renderVersion;
    return fetch(window.routes.cart_url + '?section_id=cart-drawer')
      .then(response => response.text())
      .then(html => { if (version === drawer.renderVersion) return drawer.renderContents({ sections: { 'cart-drawer': html } }, { open: false }); })
      .catch(() => {});
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.dataset.updating === 'true') return;
    drawer.dataset.updating = 'true';
    drawer.setAttribute('aria-busy', 'true');
    const input = document.getElementById('Drawer-quantity-' + line);
    const lineKey = input?.dataset.quantityLineKey;
    const deferred = this.createCartLinesUpdateEvent(quantity === 0 ? 'remove' : 'update', variantId || input?.dataset.quantityVariantId, quantity, lineKey);
    const originalQuantity = input?.getAttribute('value');
    try {
      const response = await window.VVCart.request(window.routes.cart_change_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...(lineKey ? { id: lineKey } : { line: Number(line) }),
          quantity: Number(quantity), sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || state.errors || window.cartStrings.error);
      this.resolveCartLinesUpdate(deferred, state);
      await drawer.renderContents(state, { open: false });
      if (drawer.classList.contains('active')) {
        const newInput = document.getElementById('Drawer-quantity-' + line);
        if (newInput && name) newInput.focus();
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId });
    } catch (error) {
      deferred?.reject(error);
      if (input && originalQuantity) input.value = originalQuantity;
      const errors = drawer.querySelector('#CartDrawer-CartErrors');
      if (errors) { errors.textContent = error.message; errors.setAttribute('role', 'alert'); }
    } finally {
      drawer.removeAttribute('aria-busy');
      delete drawer.dataset.updating;
    }
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', VVCartDrawerItems);
